import cssStyles from './modal.module.css';

import React, { ReactElement, useEffect, useRef, useState } from 'react';

import anime from 'animejs';
import { clearAllBodyScrollLocks, disableBodyScroll } from 'body-scroll-lock';
import ReactDOM from 'react-dom';

export interface WithModalProps {
  modal: {
    isOpen: boolean;
    close: () => void;
    isModal: boolean;
  };
}
const pickFromRect = (rect: DOMRect) => {
  const { height, left, top, width } = rect;
  return { height, width, x: left, y: top };
};

const getLastPositionStyles = ({
  maxHeight,
  maxWidth,
}: {
  maxHeight: number;
  maxWidth: number;
}) => {
  const mW = maxWidth > window.innerWidth ? window.innerWidth : maxWidth;
  const mH = maxHeight > window.innerHeight ? window.innerHeight : maxHeight;

  return {
    height: mH,
    width: mW,
    x: window.innerWidth / 2 - mW / 2,
    y: window.innerHeight / 2 - mH / 2,
  };
};

/**
 * The one thing that must stay true: the modal's content mounts ONCE per open.
 *
 * When it does not, nothing throws. The form silently re-initialises from
 * defaultData and whatever the admin typed is gone, or the trigger is swapped
 * out from under a click and the modal never opens. Both are invisible to
 * every monitor we have, so the library says so itself: one Rollbar item, from
 * the browser snippet that is already on the page, per page load.
 */
let reported = false;
const reportRemount = (detail: { [key: string]: any }) => {
  if (reported) return;
  reported = true;
  const w = window as any;
  w.Rollbar?.error?.(
    'rc-modal: modal content remounted without an open (typed edits are wiped)',
    detail
  );
};

/** Mounts once per key. A second mount under the same `openCount` is the bug. */
function MountOnce({ onMount }: { onMount: () => void }): null {
  useEffect(onMount, []);

  return null;
}

enum states {
  CLOSE = 'CLOSE',
  IDLE = 'IDLE',
  IMMEDIATELY_CLOSE = 'IMMEDIATELY_CLOSE',
  OPEN = 'OPEN',
  OPENED = 'OPENED',
}

const openState: states[] = [
  states.OPEN,
  states.OPENED,
  states.CLOSE,
  states.IMMEDIATELY_CLOSE,
];
const closingState: states[] = [states.CLOSE, states.IMMEDIATELY_CLOSE];

type ModalProps<
  M extends { [key: string]: any } | undefined,
  B extends { [key: string]: any } | undefined,
> = (M extends undefined
  ? {
      ModalComponent: (
        props: WithModalProps & { unSureWhyINeedThis: () => void }
      ) => ReactElement;
      modalProps?: undefined;
    }
  : {
      ModalComponent: (
        props: WithModalProps & { unSureWhyINeedThis: () => void } & M
      ) => ReactElement;
      modalProps: M;
    }) &
  (B extends undefined
    ? {
        ButtonComponent: (
          props: WithModalProps & { onClick: () => void }
        ) => ReactElement;
        buttonProps?: undefined;
      }
    : {
        ButtonComponent: (
          props: WithModalProps & { onClick: () => void } & B
        ) => ReactElement;
        buttonProps: B;
      });

function useModal<
  M extends { [key: string]: any } | undefined,
  B extends { [key: string]: any } | undefined,
>({
  ButtonComponent,
  ModalComponent,
  buttonProps,
  containerProps = {},
  maxHeight = 500,
  maxWidth = 700,
  modalProps,
  ms = 300,
  showButton = true,
  transitionOpacity = false,
}: {
  maxWidth?: number;
  maxHeight?: number;
  ms?: number;
  showButton?: boolean;
  transitionOpacity?: boolean;
  containerProps?: { [key: string]: any };
} & ModalProps<M, B>): [
  (props: { [key: string]: never }) => ReactElement,
  () => void,
] {
  const [state, setState] = useState<states>(states.IDLE);
  const [styles, setStyles] = useState({});
  const [renderPortal, setRenderPortal] = useState(false);
  // Bumped on every open. It keys the ModalComponent, so its form re-reads
  // defaultData once per open, and at no other time.
  const [openCount, setOpenCount] = useState(0);

  const modalContentRef = useRef<HTMLDivElement | null>(null);
  const buttonRef = useRef<HTMLDivElement | null>(null);
  // The open each content mount belonged to, and the components of the last
  // render: the two ways the content can remount behind our back.
  const mountedFor = useRef<number | null>(null);
  const lastComponents = useRef<[unknown, unknown] | null>(null);

  if (
    lastComponents.current &&
    (lastComponents.current[0] !== ButtonComponent ||
      lastComponents.current[1] !== ModalComponent)
  ) {
    // A consumer passing an inline `ModalComponent={props => …}` makes a new
    // type every render, which remounts the form exactly like the old bug did.
    reportRemount({ cause: 'component-prop-identity', state });
  }
  lastComponents.current = [ButtonComponent, ModalComponent];

  useEffect(() => {
    setRenderPortal(true);
    return () => {
      setRenderPortal(false);
    };
  }, []);

  useEffect(() => {
    if (state === states.OPEN) {
      openAnimation();
    }

    if (state === states.CLOSE) {
      closeAnimation();
    }
  }, [state]);

  const setStartData = (s?: states) => {
    if (!buttonRef.current || !buttonRef.current.nextElementSibling) return;
    const rect = pickFromRect(
      buttonRef.current.nextElementSibling.getBoundingClientRect()
    );
    setStyles({
      left: 0,
      maxHeight: rect.height,
      maxWidth: rect.width,
      opacity: transitionOpacity && s === states.OPEN ? 0 : 1,
      top: 0,
      transform: `translateX(${rect.x}px) translateY(${rect.y}px)`,
    });
    if (s) setState(s);
  };

  const openAnimation = () => {
    if (state !== states.OPEN) return;
    if (!buttonRef.current || !buttonRef.current.nextElementSibling) return;
    const rect = pickFromRect(
      buttonRef.current.nextElementSibling.getBoundingClientRect()
    );
    const to = getLastPositionStyles({
      maxHeight,
      maxWidth,
    });
    anime({
      baseFrequency: 0,
      complete: () => {
        setState(states.OPENED);
        setStyles({
          left: '50%',
          maxHeight: '90vh',
          maxWidth: to.width,
          opacity: 1,
          top: '50%',
          transform: 'translateX(-50%) translateY(-50%)',
        });
      },
      duration: ms,
      easing: 'easeInQuad',
      maxHeight: to.height,
      maxWidth: to.width,
      opacity: 1,
      targets: modalContentRef.current,
      translateX: [rect.x, to.x],
      translateY: [rect.y, to.y],
    });
  };

  const open = () => {
    if (state !== states.IDLE) return;
    setOpenCount(c => c + 1);
    setStartData(states.OPEN);
    if (buttonRef.current) {
      disableBodyScroll(buttonRef.current, {
        reserveScrollBarGap: true,
      });
    }
  };

  const closeAnimation = () => {
    if (!buttonRef.current || !modalContentRef.current) return;
    const rect = getLastPositionStyles({
      maxHeight,
      maxWidth,
    });
    if (!buttonRef.current || !buttonRef.current.nextElementSibling) return;
    const to = pickFromRect(
      buttonRef.current.nextElementSibling.getBoundingClientRect()
    );

    anime({
      baseFrequency: 0,
      complete: () => {
        setState(states.IDLE);
        setStyles({ opacity: 0 });
      },
      duration: ms,
      easing: 'easeOutQuad',
      maxHeight: [rect.height, to.height],
      maxWidth: [rect.width, to.width],
      opacity: transitionOpacity ? 0 : 1,
      targets: modalContentRef.current,
      translateX: [rect.x, to.x],
      translateY: [rect.y, to.y],
    });
  };
  const close = () => {
    if (state !== states.OPENED) return;
    setStartData(states.CLOSE);
    clearAllBodyScrollLocks();
  };

  const buildProps = (modal?: boolean): WithModalProps => ({
    modal: { close, isModal: !!modal, isOpen: openState.includes(state) },
  });

  // Modal keeps ONE identity for the life of the hook. It used to be declared
  // inline here, so every render of the consumer made a new component type and
  // React remounted the button and the whole portal (and the form in it). A
  // click on the outgoing button was lost, and a form re-initialised at the
  // end of the open fade, wiping anything typed during it. Modal takes no
  // props and renders whatever the consumer's latest render left in `render`;
  // the consumer re-rendering re-renders Modal, so it is never stale.
  const render = useRef<(() => ReactElement) | null>(null);
  render.current = () => (
    <React.Fragment>
      <div ref={buttonRef} style={{ display: 'none' }}></div>
      <ButtonComponent
        {...buildProps()}
        {...buttonProps}
        onClick={() => {
          open();
        }}
      />
      {renderPortal &&
        ReactDOM.createPortal(
          <div className={cssStyles['transform-modal']} {...containerProps}>
            <div
              className={[
                cssStyles['transform-modal__container'],
                openState.includes(state)
                  ? cssStyles['transform-modal__container--open']
                  : '',
                closingState.includes(state)
                  ? cssStyles['transform-modal__container--closing']
                  : '',
              ].join(' ')}
            >
              <div
                className={cssStyles['transform-modal__background']}
                onClick={close}
                style={{ transition: `opacity ${ms / 4}ms ease-in-out` }}
              />
              <div
                className={cssStyles['transform-modal__content']}
                style={{
                  ...styles,
                  transition: `box-shadow ${ms}ms ease-in-out`,
                }}
                ref={modalContentRef}
              >
                {showButton && (
                  <button type="reset" onClick={close}>
                    &#x2715;
                  </button>
                )}
                <div key={openCount}>
                  <MountOnce
                    onMount={() => {
                      if (mountedFor.current === openCount) {
                        reportRemount({ cause: 'remount', openCount, state });
                      }
                      mountedFor.current = openCount;
                    }}
                  />
                  <ModalComponent
                    {...buildProps(true)}
                    {...modalProps}
                    unSureWhyINeedThis={() => null}
                  />
                </div>
              </div>
            </div>
          </div>,
          document.body
        )}
    </React.Fragment>
  );
  const Modal = useRef<
    ((props: { [key: string]: never }) => ReactElement) | null
  >(null);
  if (!Modal.current) {
    Modal.current = function Modal() {
      return render.current!();
    };
  }

  return [Modal.current, open];
}

export default useModal;
